import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { formium } from '../../lib/formium';
import clsx from 'clsx';
import {
    Button,
    Typography,
    TextField,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    form: {
        fontFamily: 'inherit',
        paddingTop: '1.5em',
        borderTop: '1px solid rgb(210, 213, 217)',
    },
    formControl: {
        backgroundColor: 'rgba(0,0,0,0.05)',
        padding: '2em 1em',
        margin: '2em 0 0 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    title: {
        fontFamily: 'inherit',
        color: 'rgb(51,51,51)',
    },
    feedbackWrapper: {
        fontFamily: 'inherit',
        margin: '1em 1em',
        width: '100%',
    },
    buttonWrapper: {
        fontFamily: 'inherit',
        display: 'flex',
        justifyContent: 'center',
    },

    button: {
        color: 'rgb(51,51,51)',
        fontFamily: 'inherit',
        padding: '0.5em 1em',
        margin: '0em 0.5em',
        fontWeight: 'bold',
        textTransform: 'none',
        backgroundColor: 'white',
        borderColor: 'white',
        '&:hover': {
            backgroundColor: '#E2E2E2',
            borderColor: '#E2E2E2',
        },
    },

    buttonClicked: {
        borderColor: 'rgba(26, 145, 208, 0.87)',
        '&:hover': {
            backgroundColor: 'white',
            borderColor: 'rgba(26, 145, 208, 0.87)',
        },
    },

    submitButton: {
        color: 'white',
        marginTop: '2em',
        backgroundColor: 'rgba(26, 145, 208, 0.87)',
        borderColor: 'rgba(26, 145, 208, 0.87)',
        '&:hover': {
            backgroundColor: '#0E8ACC',
            borderColor: '#0E8ACC',
        },
    },
    textField: {
        fontFamily: 'inherit',
        width: '100%',
        maxWidth: '500px',
        margin: '1em 0em',
        '& input': {
            color: 'rgb(111,111,111)',
        },
        '& textarea': {
            color: 'rgb(111,111,111)',
        },
        '& label': {
            width: '90%',
            [theme.breakpoints.down('md')]: {
                fontSize: '0.9rem',
            },
        },
        '& label.Mui-focused': {
            color: 'rgb(111,111,111)',
        },
        '& label.Mui-focused.Mui-error': {
            color: 'red',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'rgb(111,111,111)',
            },
            '&:hover fieldset': {
                borderColor: 'rgb(111,111,111)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgb(111,111,111)',
            },
        },
        '& .MuiOutlinedInput-root.Mui-error': {
            '& fieldset': {
                borderColor: 'red !important',
            },
            '&:hover fieldset': {
                borderColor: 'red !important',
            },
        },
    },
    submitFormWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: '1.75em',
    },
    // Empty style needed to override styles
    checkBoxChecked: {},
    checkBox: {
        '&$checkBoxChecked': {
            color: 'rgba(26, 145, 208, 0.87)',
        },
    },
    // Empty style needed to override styles
    radioChecked: {},
    radio: {
        '&$radioChecked': {
            color: 'rgba(26, 145, 208, 0.87)',
        },
    },
    controlLabel: {
        fontFamily: 'inherit',
        color: 'rgb(51,51,51)',
        [theme.breakpoints.down('md')]: {
            padding: '0.5rem 0em',
        },
    },
}));

const emailFormatRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const FeedbackForm = ({ formSlug, path, title, version }) => {
    // references needed to get the data on submit of the form
    const textInputRef = useRef();
    const emailInputRef = useRef();
    const classes = useStyles();
    const [feedback, setFeedback] = useState('neutral');
    const [submitted, setSubmitted] = useState(false);
    const [radioChecked, setRadioChecked] = useState('Other');
    const [contact, setContact] = useState(false);
    const [validEmail, setValidEmail] = useState(true);

    // This handler will get all the data from the form and submit it to our Formium client
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (feedback === 'negative' && contact && !emailFormatRegex.test(emailInputRef.current.value)) {
            setValidEmail(false);
        } else {
            // Retrieve the form data
            const data = populateSubmission();
            // Change to the thank you component
            setSubmitted(true);
            setValidEmail(true);
            // Send the data to Formium client
            await formium.submitForm(formSlug, data);
        }
    };

    const handleYesClick = (e) => {
        // Reset the entire form
        setFeedback('positive');
        setRadioChecked('Other');
        setContact(false);
        setValidEmail(true);
    };

    const handleRadioChange = (e) => {
        e.preventDefault();
        setRadioChecked(e.target.value);
    };

    const handleCheckBoxChange = () => {
        setContact(!contact);
    };

    // Populate this data obj to send back to Formium
    const populateSubmission = () => {
        return {
            title: title,
            path: path,
            version: version,
            response: feedback,
            textInput: textInputRef.current.value,
            radioChecked: feedback === 'negative' ? radioChecked : undefined,
            wouldLikeToBeContacted: contact,
            email: feedback === 'negative' && contact ? emailInputRef.current.value : undefined,
        };
    };

    // Determines what text label will be used based on which radio has been checked
    const determineTextLabel = () => {
        switch (radioChecked) {
            case "This isn't what I was looking for.":
                return 'What were you looking for?';
            case "There aren't enough examples.":
                return 'What examples were you looking for?';
            case 'The information is hard to understand.':
                return 'What was difficult to understand?';
            case "The information doesn't solve my issue.":
                return 'Which issue were you trying to solve?';
            default:
                return 'What can we do to improve this page?';
        }
    };

    const renderForm = () => {
        if (feedback === 'positive') {
            return (
                <div className={classes.submitFormWrapper}>
                    <TextField
                        inputRef={textInputRef}
                        className={classes.textField}
                        multiline
                        size="medium"
                        variant="outlined"
                        label="What did you like about this page? (optional)"
                        rows={2}
                    />
                    <Button
                        type="submit"
                        variant="outlined"
                        className={clsx(classes.submitButton, classes.button, classes.yesButton)}
                    >
                        Submit
                    </Button>
                </div>
            );
        } else if (feedback === 'negative') {
            return (
                <div className={classes.submitFormWrapper}>
                    <RadioGroup value={radioChecked}>
                        <FormControlLabel
                            classes={{ label: classes.controlLabel }}
                            control={
                                <Radio
                                    classes={{ checked: classes.radioChecked, root: classes.radio }}
                                    onChange={handleRadioChange}
                                    value="This isn't what I was looking for."
                                />
                            }
                            label="This isn't what I was looking for."
                        />
                        <FormControlLabel
                            classes={{ label: classes.controlLabel }}
                            control={
                                <Radio
                                    classes={{ checked: classes.radioChecked, root: classes.radio }}
                                    onChange={handleRadioChange}
                                    value="There aren't enough examples."
                                />
                            }
                            label="There aren't enough examples."
                        />
                        <FormControlLabel
                            classes={{ label: classes.controlLabel }}
                            control={
                                <Radio
                                    classes={{ checked: classes.radioChecked, root: classes.radio }}
                                    onChange={handleRadioChange}
                                    value="The information is hard to understand."
                                />
                            }
                            label="The information is hard to understand."
                        />
                        <FormControlLabel
                            classes={{ label: classes.controlLabel }}
                            control={
                                <Radio
                                    classes={{ checked: classes.radioChecked, root: classes.radio }}
                                    onChange={handleRadioChange}
                                    value="The information doesn't solve my issue."
                                />
                            }
                            label="The information doesn't solve my issue."
                        />
                        <FormControlLabel
                            classes={{ label: classes.controlLabel }}
                            control={
                                <Radio
                                    classes={{ checked: classes.radioChecked, root: classes.radio }}
                                    onChange={handleRadioChange}
                                    value="Other"
                                />
                            }
                            label="Other"
                        />
                    </RadioGroup>
                    <TextField
                        inputRef={textInputRef}
                        required={radioChecked === 'Other'}
                        className={classes.textField}
                        multiline
                        size="medium"
                        variant="outlined"
                        label={determineTextLabel()}
                        rows={2}
                    />
                    <FormControlLabel
                        classes={{ label: classes.controlLabel }}
                        control={
                            <Checkbox
                                classes={{ checked: classes.checkBoxChecked, root: classes.checkBox }}
                                checked={contact}
                            />
                        }
                        onChange={handleCheckBoxChange}
                        label="Would you like to be contacted?"
                    />
                    {contact && (
                        <TextField
                            error={!validEmail}
                            inputRef={emailInputRef}
                            required
                            className={classes.textField}
                            size="small"
                            variant="outlined"
                            label="Email Address"
                            onChange={() => setValidEmail(true)}
                        />
                    )}
                    <Button type="submit" variant="outlined" className={clsx(classes.submitButton, classes.button)}>
                        Submit
                    </Button>
                </div>
            );
        }
    };

    return (
        <form onSubmit={handleSubmit} className={classes.form}>
            <FormControl className={classes.formControl}>
                {!submitted ? (
                    <>
                        <Typography variant="subtitle1" className={classes.title}>
                            Was this page helpful?
                        </Typography>
                        <div className={classes.feedbackWrapper}>
                            <div className={classes.buttonWrapper}>
                                <Button
                                    variant="outlined"
                                    disabled={submitted}
                                    className={
                                        feedback === 'positive'
                                            ? clsx(classes.button, classes.buttonClicked)
                                            : classes.button
                                    }
                                    onClick={handleYesClick}
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={submitted}
                                    className={
                                        feedback === 'negative'
                                            ? clsx(classes.button, classes.buttonClicked)
                                            : classes.button
                                    }
                                    onClick={() => setFeedback('negative')}
                                >
                                    No
                                </Button>
                            </div>
                            {renderForm()}
                        </div>
                    </>
                ) : (
                    <Typography variant="subtitle1" className={classes.title}>
                        Thank you for your feedback!
                    </Typography>
                )}
            </FormControl>
        </form>
    );
};
export default FeedbackForm;
